import React, { useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core'
import Page from '../../components/Page'
import KesDialog from './KesDialog'
import AtlaDialog from './AtlaDialog'
import AdmitDialog from './AdmitDialog'
import {
  messageFrom,
  previewEBelge,
  readBlobError,
  useAdmitEBelge,
  useEBelgeBacklog,
  useEBelgeQueue,
  useIssueEBelge,
  useSkipEBelge,
} from '../../api/ebelge'

// Sales that carry no document at all. Not a queue status and deliberately not
// served by the queue endpoint: a queue row is a prepared document with a legal
// clock running on it, and one of these is a sale nobody has decided anything
// about yet. One table under one deadline would be wrong about half its rows.
const BACKLOG_TAB = 'backlog'
const BACKLOG_META = {
  label: 'Geçmiş satışlar',
  hint:
    'Entegrasyondan önceki satışlar. Otomatik olarak faturaya alınmazlar; ' +
    'tarihini siz onaylayarak tek tek alırsınız. Bu ekranda fatura kesilmez.',
}

// Statuses whose last_error is a real failure rather than the informational
// "Kes bekliyor" note the awaiting-approval path leaves behind.
const FAILURE_STATUSES = ['FAILED', 'REJECTED']

// Currency-aware: Genesus documents are USD, so a hardcoded "TL" would label
// every amount in the queue with the wrong unit.
const money = (v, currency = 'TRY') =>
  `${Number(v).toFixed(2).replace('.', ',')} ${currency === 'TRY' ? 'TL' : currency}`
// Full date, unlike shortDay: a backlog row can be a year old, and dd.mm alone
// would render a 2025 sale as indistinguishable from a 2026 one.
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '-')
const shortDay = (iso) => (iso ? new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }) : '-')

/**
 * Remaining hours under VUK 231/5, or how far past it we already are.
 *
 * Rendered as a countdown rather than the deadline itself because the question
 * an operator is actually asking is "does this wait until tomorrow", and a date
 * makes them do the arithmetic.
 */
function Sure({ row }) {
  if (row.ettn) return <span>-</span>
  const h = row.hours_left
  if (h < 0) {
    return (
      <Tooltip title="Yasal 7 günlük süre doldu">
        <Chip size="small" color="error" label={`${-h}s geçti`} />
      </Tooltip>
    )
  }
  return (
    <Chip
      size="small"
      color={h < 48 ? 'warning' : 'default'}
      variant={h < 48 ? 'filled' : 'outlined'}
      label={`${h}s`}
    />
  )
}

export default function EBelgeView() {
  const [tab, setTab] = useState('pending')
  const [kesRow, setKesRow] = useState(null)
  const [atlaRow, setAtlaRow] = useState(null)
  const [admitRow, setAdmitRow] = useState(null)
  const [actionError, setActionError] = useState('')
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState('')

  const onBacklog = tab === BACKLOG_TAB
  // The queue endpoint does not know the backlog tab and answers 400 for it.
  // Asking anyway put react-query into `error`, which bypasses
  // keepPreviousData, so `data` went undefined and the tab bar collapsed to
  // just this one tab - stranding the operator with no way back.
  const queueTab = onBacklog ? 'pending' : tab
  const { data, isLoading, isError, error, refetch, isFetching } = useEBelgeQueue(queueTab)
  const backlogQuery = useEBelgeBacklog(onBacklog)
  const issue = useIssueEBelge()
  const skip = useSkipEBelge()
  const admit = useAdmitEBelge()

  const tabs = { ...(data?.tabs || {}), [BACKLOG_TAB]: BACKLOG_META }
  const counts = { ...(data?.counts || {}), [BACKLOG_TAB]: backlogQuery.data?.total ?? '' }
  const rows = data?.results || []
  const backlogRows = backlogQuery.data?.results || []

  const onAdmit = async (orderDate) => {
    setActionError('')
    try {
      const created = await admit.mutateAsync({ paymentPk: admitRow.payment_pk, orderDate })
      setAdmitRow(null)
      // Where it ACTUALLY landed. due_at runs from the sale, so a month-old
      // sale is past its seven days the moment it is admitted and lands in
      // "Süresi geçen" - which is true and worth seeing. Sending the operator
      // to "Kes bekleyen" showed them an empty tab and no sign it had worked.
      setTab(created?.overdue ? 'overdue' : 'pending')
    } catch (err) {
      setActionError(messageFrom(err, 'Satış faturaya alınamadı.'))
    }
  }

  const onKes = async (issueDate, orderDate) => {
    setActionError('')
    try {
      await issue.mutateAsync({ id: kesRow.id, issueDate, orderDate })
      setKesRow(null)
    } catch (err) {
      // Deliberately kept in the dialog rather than shown as a toast that
      // disappears. A failed Kes is the moment an operator most needs to read
      // something before acting again.
      setActionError(messageFrom(err, 'Fatura kesilemedi.'))
    }
  }

  const onPreview = async (issueDate, orderDate) => {
    setPreviewError('')
    setPreviewing(true)
    try {
      const url = await previewEBelge({ id: kesRow.id, issueDate, orderDate })
      // A new tab rather than an embedded viewer: the operator wants it beside
      // the dialog to compare against, not covering it.
      window.open(url, '_blank', 'noopener')
    } catch (err) {
      setPreviewError(await readBlobError(err, 'Önizleme oluşturulamadı.'))
    } finally {
      setPreviewing(false)
    }
  }

  const onAtla = async (reason) => {
    setActionError('')
    try {
      await skip.mutateAsync({ id: atlaRow.id, reason })
      setAtlaRow(null)
    } catch (err) {
      setActionError(messageFrom(err, 'İşlem tamamlanamadı.'))
    }
  }

  return (
    <Page title="Faturalar">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h4">Faturalar</Typography>
          <Button onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Yenileniyor…' : 'Yenile'}
          </Button>
        </Stack>

        {/*
          Only shown when this is NOT plain production. Chrome that is always
          there gets ignored; a banner that appears only sometimes gets read,
          and the thing it says is whether pressing Kes files anything at all.
        */}
        {data && data.environment !== 'PROD' && (
          <Alert severity={data.dry_run ? 'warning' : 'info'} sx={{ mb: 2 }}>
            <b>{data.environment}</b>
            {data.dry_run && ': bu ortamda "Kes" belge oluşturmaz, istek gönderilmez.'}
          </Alert>
        )}

        {/*
          The only place a stopped cron can be noticed.

          Every other detector in the lane - including the one that watches for
          sales with no document - is a query the hourly job runs, so a job that
          never fires takes its own watchdog down with it and says nothing.
          This panel runs in the web process, which is up either way.
        */}
        {data?.tick?.stale && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <b>Otomatik iş çalışmıyor.</b>{' '}
            {data.tick.last_tick_at
              ? `Son başarılı çalışma ${Math.round(data.tick.hours_since)} saat önce.`
              : 'Hiç çalışmamış.'}{' '}
            Yeni satışlar için fatura satırı oluşturulmuyor olabilir; 7 günlük yasal süre işliyor.
          </Alert>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 1 }}>
          {Object.entries(tabs).map(([key, meta]) => (
            <Tab
              key={key}
              value={key}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{meta.label}</span>
                  <Chip
                    size="small"
                    label={counts[key] ?? 0}
                    color={key === 'overdue' && counts[key] > 0 ? 'error' : 'default'}
                  />
                </Stack>
              }
            />
          ))}
        </Tabs>

        {tabs[tab]?.hint && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {tabs[tab].hint}
          </Typography>
        )}

        {onBacklog ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Müşteri</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                  <TableCell>Ödeme tarihi</TableCell>
                  <TableCell>Kaynak</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backlogQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5}>Yükleniyor…</TableCell>
                  </TableRow>
                )}
                {backlogQuery.isError && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Alert severity="error" sx={{ my: 1 }}>
                        Geçmiş satışlar yüklenemedi, bu liste GÜNCEL DEĞİL.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
                {!backlogQuery.isLoading && !backlogQuery.isError && backlogRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                        Faturasız satış yok.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {backlogRows.map((b) => (
                  <TableRow key={b.payment_pk} hover>
                    <TableCell>
                      <Typography variant="body2">{b.organisation || b.person_email}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'fontFamilyMono' }}>
                        {b.payment_id}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {b.amount} {b.currency}
                      {b.charged_try && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          {b.charged_try} TL
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{b.suggested_order_date ? day(b.suggested_order_date) : '-'}</TableCell>
                    <TableCell>
                      {/*
                        Whether that date is a recorded fact or an inference.
                        Both render as a date, and only one of them is the
                        taxable event, so the difference has to be on the row
                        and not only inside the dialog.
                      */}
                      {b.suggested_source === 'confirmed_at' && <Chip size="small" label="kayıtlı" />}
                      {b.suggested_source === 'created_at' && (
                        <Tooltip title="Ödemenin başlatıldığı an. Satış tarihi bu olmayabilir; onaylamanız gerekir.">
                          <Chip size="small" color="warning" label="tahmini" />
                        </Tooltip>
                      )}
                      {!b.suggested_source && <Chip size="small" color="error" label="tarih yok" />}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" disabled={!b.billable} onClick={() => setAdmitRow(b)}>
                        Faturaya al
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Müşteri</TableCell>
                  <TableCell>VKN</TableCell>
                  <TableCell>Tür</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                  <TableCell>Satış</TableCell>
                  <TableCell>Süre</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={8}>Yükleniyor…</TableCell>
                  </TableRow>
                )}
                {/*
                An unreachable API must not read as an empty queue. "Bu sekmede
                kayıt yok" on a failed request tells an operator there is
                nothing to bill, which is the one wrong thing to tell them
                inside a seven-day legal deadline.
              */}
                {isError && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Alert severity="error" sx={{ my: 1 }}>
                        Kuyruk yüklenemedi, bu liste GÜNCEL DEĞİL.
                        {error?.response?.data?.error ? ` ${error.response.data.error}` : ''}
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !isError && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                        Bu sekmede kayıt yok.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2">{row.organisation}</Typography>
                      {row.buyer_title && row.buyer_title !== row.organisation && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {row.buyer_title}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'fontFamilyMono' }}>{row.buyer_vkn || '-'}</TableCell>
                    <TableCell>{row.doc_type || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money(row.amount_total, row.currency)}
                    </TableCell>
                    {/*
                      order_at first. sale_at is payment.confirmed_at, which is
                      NULL on every admitted backlog row - so this column
                      rendered "-" for exactly the sales whose date an operator
                      had just typed in, which reads as the edit not having
                      worked.
                    */}
                    <TableCell>{shortDay(row.order_at || row.sale_at)}</TableCell>
                    <TableCell>
                      <Sure row={row} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{row.status_label}</span>
                        {row.create_attempted_at && !row.ettn && (
                          <Tooltip title="Kesim isteği gönderilmiş, sonucu alınamamış. Elle kesmeden önce portalı kontrol edin.">
                            <Chip size="small" color="error" label="?" />
                          </Tooltip>
                        )}
                      </Stack>
                      {row.invoice_number && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'fontFamilyMono' }}>
                          {row.invoice_number}
                        </Typography>
                      )}
                      {/*
                      Shown on every row that carries one. It used to be gated on
                      !can_issue, which hid it on precisely the rows the
                      "Takılanlar" tab exists for: FAILED rows are claimable, so
                      can_issue is true and the reason they failed was the one
                      thing not on screen. Colour separates a real failure from
                      the informational "Kes bekliyor" note.
                    */}
                      {row.last_error && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: FAILURE_STATUSES.includes(row.status) ? 'error.main' : 'text.secondary',
                          }}
                        >
                          {row.last_error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.can_issue && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="contained" onClick={() => setKesRow(row)}>
                            Kes
                          </Button>
                          <Button size="small" onClick={() => setAtlaRow(row)}>
                            Atla
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <KesDialog
        row={kesRow}
        open={Boolean(kesRow)}
        busy={issue.isLoading}
        error={actionError}
        onPreview={onPreview}
        previewing={previewing}
        previewError={previewError}
        onClose={() => {
          setKesRow(null)
          setActionError('')
          setPreviewError('')
        }}
        onConfirm={onKes}
      />
      <AtlaDialog
        row={atlaRow}
        open={Boolean(atlaRow)}
        busy={skip.isLoading}
        error={actionError}
        onClose={() => {
          setAtlaRow(null)
          setActionError('')
        }}
        onConfirm={onAtla}
      />
      <AdmitDialog
        row={admitRow}
        open={Boolean(admitRow)}
        onClose={() => {
          setAdmitRow(null)
          setActionError('')
        }}
        onConfirm={onAdmit}
        busy={admit.isLoading}
        error={actionError}
      />
    </Page>
  )
}
